import { useCallback, useEffect, useState } from 'react';

import usePrevious from './usePrevious';

const getDiffDevices = (oldDevices, newDevices) => {
  return newDevices?.filter(dv => !oldDevices?.map(d => d.deviceId).includes(dv.deviceId));
};

function useDeviceChanged({ zoomClient, mediaStream, deviceAdded, deviceRemoved }) {
  const [cameras, setCameras] = useState([]);
  const oldCameras = usePrevious(cameras);
  const [speakers, setSpeakers] = useState([]);
  const [mics, setMics] = useState([]);
  const oldMics = usePrevious(mics);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [activeMic, setActiveMic] = useState(null);
  const [activeCamera, setActiveCamera] = useState(null);

  const deviceChange = useCallback(async () => {
    const camerasUpdated = mediaStream?.getCameraList();
    const micsUpdated = mediaStream?.getMicList();
    const speakersUpdated = mediaStream?.getSpeakerList();

    setCameras(camerasUpdated);
    setSpeakers(speakersUpdated);
    setMics(micsUpdated);
    setActiveSpeaker(mediaStream?.getActiveSpeaker());
    setActiveMic(mediaStream?.getActiveMicrophone());
    setActiveCamera(mediaStream?.getActiveCamera());
  }, [mediaStream]);

  const selectMicrophone = useCallback(
    deviceId => {
      mediaStream?.switchMicrophone(deviceId);
      setActiveMic(deviceId);
    },
    [mediaStream],
  );

  const selectSpeaker = useCallback(
    deviceId => {
      mediaStream?.switchSpeaker(deviceId);
      setActiveSpeaker(deviceId);
    },
    [mediaStream],
  );

  const selectCamera = useCallback(
    deviceId => {
      mediaStream?.switchCamera(deviceId);
      setActiveCamera(deviceId);
    },
    [mediaStream],
  );

  useEffect(() => {
    if (!zoomClient) return;
    zoomClient.on('device-change', deviceChange);
    return () => {
      zoomClient.off('device-change', deviceChange);
    };
  }, [deviceChange, zoomClient]);

  useEffect(() => {
    const addedCameras = getDiffDevices(oldCameras, cameras);
    const removedCameras = getDiffDevices(cameras, oldCameras);
    const addedMics = getDiffDevices(oldMics, mics);
    const removedMics = getDiffDevices(mics, oldMics);
    if (addedCameras?.length > 0) {
      deviceAdded('cam', oldCameras, cameras, addedCameras);
    }
    if (removedCameras?.length > 0) {
      deviceRemoved('cam', oldCameras, cameras, removedCameras);
    }
    if (addedMics?.length > 0) {
      deviceAdded('mic', oldMics, mics, addedMics);
    }
    if (removedMics?.length > 0) {
      deviceRemoved('mic', oldMics, mics, removedMics);
    }
  }, [oldCameras, cameras, oldMics, mics, deviceAdded, deviceRemoved]);

  return {
    cameras,
    speakers,
    mics,
    activeSpeaker,
    activeCamera,
    activeMic,
    selectMicrophone,
    selectSpeaker,
    selectCamera,
  };
}

export default useDeviceChanged;
